import React, { useState, useEffect } from 'react';
import styles from '../styles/DecisionTool.styles';
import SegmentPreferenceIndex from './SegmentPreferenceIndex';
import SegmentInfo from './SegmentInformation';


const AdvertisingTable = ({
    segmentsOptions,
    selectedSegments,
    handleSegmentSelect,
    handleSegmentRemove,
    attributesList,
    upgradedBikeValues,
    advertisingBudgets,
    handleAdvertisingChange,
    roundNumber,
}) => {
    const [showSegmentPreference, setShowSegmentPreference] = useState(false);
    const [showSegmentInfo, setShowSegmentInfo] = useState(false);
    const [isAdError, setIsAdError] = useState(false); // State for error checking

    const toggleSegmentPreference = () => {
        setShowSegmentPreference((prev) => !prev);
    };

    const toggleSegmentInfo = () => {
        setShowSegmentInfo((prev) => !prev);
    };

    // Calculate total advertising cost (sum of Performance and Brand categories)
    const calculateTotalAdCost = () => {
        const performanceTotal = Object.values(advertisingBudgets.Performance).reduce((sum, value) => sum + value, 0);
        const brandTotal = Object.values(advertisingBudgets.Brand).reduce((sum, value) => sum + value, 0);
        return performanceTotal + brandTotal;
    };

    // Check if the advertising budget exceeds the limit
    useEffect(() => {
        // Calculate the total advertising cost
        const totalAdCost = calculateTotalAdCost();

        // Determine the advertising budget limit based on the round number
        const adBudgetLimit = roundNumber === 3 ? 2000000 : 500000;

        // Check if the total advertising cost exceeds the budget limit
        if (totalAdCost > adBudgetLimit) {
            setIsAdError(true);
        } else {
            setIsAdError(false);
        }
    }, [advertisingBudgets]); // Recalculate when advertising budgets or roundNumber change


    // Format the total advertising cost as a dollar value with commas
    const formattedTotalAdCost = `$${calculateTotalAdCost().toLocaleString()}`;

    return (
        <div>
            <div className={styles.headerContainer}>
                <h2>Advertising</h2>
                <button onClick={toggleSegmentPreference} className={styles.toggleButton} style={{ marginBottom: '10px' }} >
                    {showSegmentPreference ? 'Hide Population size and Preference Overview' : 'View Population size and Preference Overview'}
                </button>
                <button onClick={toggleSegmentInfo} className={styles.toggleButton} style={{ marginBottom: '10px', marginLeft: '5px' }} >
                    {showSegmentInfo ? 'Hide Advertising Cost by Audience' : 'View Advertising Cost by Audience'}
                </button>
            </div>

            {
                showSegmentPreference && (
                    <div className={styles.collapsibleSection}>
                        <SegmentPreferenceIndex />
                    </div>
                )
            }
            {
                showSegmentInfo && (
                    <div className={styles.collapsibleSection}>
                        <SegmentInfo />
                    </div>
                )
            }

            <div>
                <span>Choose as many segment(s) to target. Click to add:  </span>
                <select
                    style={{
                        borderRadius: '4px',
                        padding: '6px 10px',
                        border: '1px solid #ccc',
                        outline: 'none',
                        marginBottom: '4px',
                        marginTop: '8px',
                    }}
                    onChange={handleSegmentSelect}>
                    <option value="">Select a segment</option>
                    {segmentsOptions.map((segment) => (
                        <option key={segment} value={segment}>
                            {segment}
                        </option>
                    ))}
                </select>

                <div className={styles.selectedSegmentsContainer}>
                    {selectedSegments.map((segment) => (
                        <styles.segmentTag key={segment}>
                            {segment}
                            <styles.removeSegment onClick={() => handleSegmentRemove(segment)}> x </styles.removeSegment>
                        </styles.segmentTag>
                    ))}
                </div>
                <p></p>
                <span
                    style={{
                        marginBottom: '12px',
                        display: 'inline-block', // or 'block'
                    }}
                >
                    Choose your advertising strategy.
                </span>
            </div>

            <table className={styles.tradeoffTable}>
                <thead>
                    <tr>
                        <styles.att>Attribute</styles.att>
                        <styles.th>Broad/General</styles.th>  {/* New column */}
                        {attributesList.map((columnAttribute) => (
                            <styles.th key={columnAttribute}>{columnAttribute}</styles.th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <styles.field2>Final Bike</styles.field2>
                        <styles.td></styles.td> {/* No value in the "Upgraded Bike" row for Broad/General */}
                        {attributesList.map((columnAttribute) => (
                            <styles.td key={columnAttribute}>{upgradedBikeValues[columnAttribute]}</styles.td>
                        ))}
                    </tr>

                    <tr>
                        <styles.field2>Performance Advertising</styles.field2>
                        <styles.td> {/* New dropdown for "Broad/General" */}
                            <select
                                value={advertisingBudgets.Performance['Broad']}
                                onChange={(e) => handleAdvertisingChange('Performance', 'Broad', e.target.value)}
                            >
                                {[0, 50000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000].map((value) => (
                                    <option key={value} value={value}>
                                        ${value.toLocaleString()}
                                    </option>
                                ))}
                            </select>
                        </styles.td>
                        {attributesList.map((attribute) => (
                            <styles.td key={attribute}>
                                <select
                                    value={advertisingBudgets.Performance[attribute]}
                                    onChange={(e) => handleAdvertisingChange('Performance', attribute, e.target.value)}
                                >
                                    {[0, 50000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000].map((value) => (
                                        <option key={value} value={value}>
                                            ${value.toLocaleString()}
                                        </option>
                                    ))}
                                </select>
                            </styles.td>
                        ))}
                    </tr>

                    <tr>
                        <styles.field2>Brand Advertising</styles.field2>
                        <styles.td> {/* New dropdown for "Broad/General" */}
                            <select
                                value={advertisingBudgets.Brand['Broad']}
                                onChange={(e) => handleAdvertisingChange('Brand', 'Broad', e.target.value)}
                            >
                                {[0, 50000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000].map((value) => (
                                    <option key={value} value={value}>
                                        ${value.toLocaleString()}
                                    </option>
                                ))}
                            </select>
                        </styles.td>
                        {attributesList.map((attribute) => (
                            <styles.td key={attribute}>
                                <select
                                    value={advertisingBudgets.Brand[attribute]}
                                    onChange={(e) => handleAdvertisingChange('Brand', attribute, e.target.value)}
                                >
                                    {[0, 50000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000].map((value) => (
                                        <option key={value} value={value}>
                                            ${value.toLocaleString()}
                                        </option>
                                    ))}
                                </select>
                            </styles.td>
                        ))}
                    </tr>
                </tbody>
            </table>

            <p> </p>

            {/* Total Advertising Cost */}
            <div className={styles.totalContainer}>
                <styles.label>TOTAL ADVERTISING COST :</styles.label>
                <styles.sumInput
                    type="text"
                    value={formattedTotalAdCost}  // Display the formatted total cost
                    readOnly
                    style={{
                        color: isAdError ? 'red' : 'darkgreen',  // Error color (red) if exceeds budget
                        fontSize: '16px',
                        width: '120px',  // Adjust width to fit larger numbers
                        padding: '5px',
                    }}
                />
                {isAdError && (
                    <span style={{ color: 'red', marginLeft: '10px' }}>
                        ERROR: Advertising Budget Exceeded
                    </span>
                )}
            </div>

            <div>
                <p></p>
            </div>
            <div>
                <p></p>
            </div>
        </div>
    );
};

export default AdvertisingTable;
